import React, { useEffect, useState, useCallback } from 'react';
import { Form, Select, Button, Space, message, Row, Col, Breadcrumb, Flex, Modal, Upload, Table } from 'antd';

import { HomeOutlined, DeleteOutlined, EditOutlined, UploadOutlined } from '@ant-design/icons';
import { apiRequest } from '../../../../service/apiRequest';
import { showError, showSuccess } from '../../../../components/errorHandler';

import Cropper from 'react-easy-crop';
import getCroppedImg from '../../../../utils/cropImage'; // a util to get cropped image blob (code below)
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';

interface Organization {
  organization_id: number;
  organization_name: string;
}

interface Class {
  class_id: number;
  class_name: string;
}
interface Student {
  student_id: number;
  student_name: string;
  student_father_name: string;
  student_class: string;
  class_name: string;
  idcard_delivered?: boolean;
  image_url?: string;
}
interface User {
  user_id: number;
  user_name: string;
  idcard_delivered?: boolean;
  image_url?: string;
}
interface IDCardRecord {
  key: string;
  id: number;
  name: string;
  image_url?: string | null;
  idcard_delivered?: boolean;
}
const IdCardMaster: React.FC = () => {
  const [form] = Form.useForm();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadingId, setUploadingId] = useState<number | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [fileList, setFileList] = useState<any[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [userType, setUserType] = useState<string | undefined>(undefined);

  const [brightness, setBrightness] = useState(100); // in %
  const [contrast, setContrast] = useState(100);     // in %


  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<{ x: number; y: number; width: number; height: number; } | null>(null);




  const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);

  const [isPreviewMode, setIsPreviewMode] = useState(false);

  const uploadFileUrlRef = React.useRef<string | null>(null);
  const croppedImageUrlRef = React.useRef<string | null>(null);

  const [uploadFileUrl, setUploadFileUrl] = useState<string | null>(null);

  const onCropComplete = (_: any, croppedAreaPixels: { x: number; y: number; width: number; height: number }) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };




  // Load organizations on mount
  useEffect(() => {
    apiRequest<Organization[]>({ method: 'get', url: '/organization' })
      .then(setOrganizations)
      .catch(() => showError('Failed to load organizations'));
  }, []);



  // Load classes if user_type is student and organization is selected
  useEffect(() => {
    const orgId = form.getFieldValue('organization_id');
    if (userType === 'student' && orgId) {
      apiRequest<Class[]>({ method: 'get', url: `/classes?organization_id=${orgId}` })
        .then(setClasses)
        .catch(() => showError('Failed to load classes'));
    } else {
      setClasses([]);
      form.setFieldsValue({ class_id: undefined });
    }
  }, [userType, form.getFieldValue('organization_id')]);

  useEffect(() => {
    if (uploadFile) {
      const url = URL.createObjectURL(uploadFile);
      setUploadFileUrl(url);

      return () => {
        URL.revokeObjectURL(url);
        setUploadFileUrl(null);
      };
    } else {
      setUploadFileUrl(null);
    }
  }, [uploadFile]);

  // Handler for Show List button
  const fetchList = async () => {
    const organization_id = form.getFieldValue('organization_id');
    const type = form.getFieldValue('user_type');
    const class_id = form.getFieldValue('class_id');

    if (!organization_id) {
      showError('Please select an organization');
      return;
    }
    if (!type) {
      showError('Please select a type');
      return;
    }
    if (type === 'student' && !class_id) {
      showError('Please select a class');
      return;
    }

    showSuccess('Filters applied! Implement fetching data.');

    setLoading(true);
    try {
      if (type === 'student') {
        const classId = form.getFieldValue('class_id');
        if (!classId) {
          message.warning('Please select class');
          setLoading(false);
          return;
        }
        const data = await apiRequest<Student[]>({
          method: 'get',
          url: `/get-idcard-students?organization_id=${organization_id}&class_id=${classId}`,
        });
        setStudents(data);
        setUsers([]);
      } else {
        const data = await apiRequest<User[]>({
          method: 'get',
          url: `/users?organization_id=${organization_id}`,
        });
        setUsers(data);
        setStudents([]);
      }
    } catch {
      showError('Failed to fetch list');
    } finally {
      setLoading(false);
    }
  };

  
  const openUploadModal = (id: any) => {
    setUploadingId(id);
    setUploadModalVisible(true);
  };
  const onMarkDelivered = async (id: number, isStudent: boolean) => {
    setLoading(true);
    try {
      await apiRequest({
        method: 'post',
        url: '/mark-idcard-delivered',
        data: { id, type: isStudent ? 'student' : 'user' },
      });
      showSuccess('Marked as delivered');
      fetchList();
    } catch {
      showError('Failed to mark delivered');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      showError('No file selected');
      return;
    }
    if (uploadingId === null || uploadingId === undefined) {
      showError("No user/student selected for upload");
      return;
    }
    if (!croppedAreaPixels) {
      showError('Please crop the image before uploading.');
      return;
    }

    setLoading(true);
    try {
      const croppedImageBlob = await getCroppedImg(
        URL.createObjectURL(uploadFile),
        croppedAreaPixels,
        brightness,
        contrast
      );

      const formData = new FormData();
      formData.append('attachments', croppedImageBlob, 'cropped.jpg');
      if (userType) {
        formData.append('ref_type', userType);
      }

      if (userType === 'student') {
        formData.append('student_id', uploadingId.toString());
      } else {
        formData.append('user_id', uploadingId.toString());
      }

      await apiRequest({
        method: 'post',
        url: '/upload-idcard-image',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      showSuccess('Image uploaded');
      fetchList();
      setUploadModalVisible(false);
      setUploadFile(null);
      setUploadingId(null);
    } catch {
      showError('Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintSelected = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Select at least one record');
      return;
    }
    const ids = selectedRowKeys.join(',');
    window.open(`/print-idcard/${userType}/multiple?ids=${ids}`, '_blank');
  };

  const handleDoublePrint = (id: number) => {
    window.open(`/print-idcard/${userType}/double/${id}`, '_blank');
  };

  const commonColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Image',
      dataIndex: 'image_url',
      key: 'image',
      render: (url: string | null) => url ? (
        <img src={url} alt="ID" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }} />
      ) : 'No Image',
    },
    {
      title: 'Status',
      dataIndex: 'idcard_delivered',
      key: 'status',
      render: (delivered: boolean) => (
        delivered ? <span style={{ color: 'green' }}>Delivered</span> : <span style={{ color: 'orange' }}>Pending</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            onClick={() => openUploadModal(record.student_id || record.user_id)}>
            {record.image_url ? 'Replace Image' : 'Upload Image'}
          </Button>

          <Button
            disabled={!record.image_url}
            onClick={() => window.open(`/print-idcard/${userType}/${record.student_id || record.user_id}`, '_blank')}
          >
            Reprint
          </Button>

          <Button
            type={record.idcard_delivered ? 'default' : 'primary'}
            disabled={record.idcard_delivered}
            onClick={() => onMarkDelivered(record.id, userType === 'student')}
          >
            {record.idcard_delivered ? 'Delivered' : 'Mark Delivered'}
          </Button>
        </Space>
      ),
    },
  ];

  const dataSource: IDCardRecord[] = userType === 'student'
    ? students.map(s => ({
      key: `s-${s.student_id}`,
      id: s.student_id,
      name: s.student_name,
      image_url: s.image_url,
      idcard_delivered: s.idcard_delivered,
    }))
    : users.map(u => ({
      key: `u-${u.user_id}`,
      id: u.user_id,
      name: u.user_name,
      image_url: u.image_url,
      idcard_delivered: u.idcard_delivered,
    }));

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => setSelectedRowKeys(selectedKeys),
  };




  const showCroppedImage = useCallback(async () => {
    if (!uploadFile || !croppedAreaPixels) return;

    try {
      const croppedImageBlob = await getCroppedImg(
        URL.createObjectURL(uploadFile),
        croppedAreaPixels
      );

      // Create a URL for the cropped image blob
      const croppedImageUrl = URL.createObjectURL(croppedImageBlob);
      setCroppedImageUrl(croppedImageUrl);
    } catch (e) {
      console.error(e);
    }
  }, [uploadFile, croppedAreaPixels]);
  const downloadImage = () => {
    if (!croppedImageUrl) return;

    const link = document.createElement('a');
    link.href = croppedImageUrl;
    link.download = 'idcard-cropped.jpg'; // filename here
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleModalClose = () => {
    setUploadModalVisible(false);
    setUploadFile(null);
    setFileList([]);
    setUploadingId(null);
    setIsPreviewMode(false);
    setCroppedImageUrl(null);
    setUploadFileUrl(null);
    if (uploadFileUrlRef.current) {
      URL.revokeObjectURL(uploadFileUrlRef.current);
      uploadFileUrlRef.current = null;
    }
    if (croppedImageUrlRef.current) {
      URL.revokeObjectURL(croppedImageUrlRef.current);
      croppedImageUrlRef.current = null;
    }
  };
  useEffect(() => {
    console.log('uploadingId:', uploadingId);
  }, [uploadingId]);

  useEffect(() => {
    console.log('dataSource:', dataSource);
  }, [dataSource]);
  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View ID Card</Breadcrumb.Item>
        </Breadcrumb>


      </Space>
      <Form form={form} layout="inline" style={{ marginTop: 20, width: '100%', justifyContent: 'flex-start', alignItems: 'center' }}>
        <Form.Item
          name="organization_id"
          rules={[{ required: true, message: 'Please select organization' }]}
          style={{ minWidth: 250 }}
        >
          <Select
            placeholder="Select Organization"
            options={organizations.map(org => ({ label: org.organization_name, value: org.organization_id }))}
            onChange={() => {
              form.setFieldsValue({ user_type: undefined, class_id: undefined });
              setClasses([]);
              setUserType(undefined);
            }}
            style={{ width: 250 }}
          />
        </Form.Item>

        <Form.Item
          name="user_type"
          rules={[{ required: true, message: 'Please select type' }]}
          style={{ minWidth: 200 }}
        >
          <Select
            placeholder="Select Type"
            options={[
              { label: 'Student', value: 'student' },
              { label: 'User', value: 'user' },
            ]}
            onChange={val => {
              form.setFieldsValue({ class_id: undefined });
              setUserType(val);
              setClasses([]);
            }}
            style={{ width: 200 }}
          />
        </Form.Item>

        {userType === 'student' && (
          <Form.Item
            name="class_id"
            rules={[{ required: true, message: 'Please select class' }]}
            style={{ minWidth: 250 }}
          >
            <Select
              placeholder="Select Class"
              options={classes.map(cls => ({ label: cls.class_name, value: cls.class_id }))}
              disabled={classes.length === 0}
              style={{ width: 250 }}
            />
          </Form.Item>
        )}

        <Form.Item>
          <Button type="primary" onClick={fetchList}>
            Show List
          </Button>
        </Form.Item>

        <Button
          disabled={selectedRowKeys.length === 0}
          onClick={handlePrintSelected}
          style={{ marginLeft: 8, marginBottom: 16 }}
        >
          Print Selected
        </Button>
      </Form>

      <Table
        dataSource={dataSource as any[]}
        loading={loading}
        rowKey={(record) =>
          record.student_id !== undefined
            ? `s-${record.student_id}`
            : `u-${record.user_id}`
        }
        rowSelection={rowSelection}
        columns={[
          ...commonColumns.slice(0, -1),
          {
            ...commonColumns[commonColumns.length - 1],
            render: (_: any, record: any) => (
              <Space>
                <Button onClick={() => {
                  setUploadingId(record.id);
                  setUploadModalVisible(true);
                }}>
                  {record.image_url ? 'Replace Image' : 'Upload Images'}
                </Button>

                <Button
                  disabled={!record.image_url}
                  onClick={() => window.open(`/print-idcard/${userType}/${record.student_id || record.user_id}`, '_blank')}
                >
                  Reprint
                </Button>

                <Button
                  onClick={() => handleDoublePrint(record.student_id || record.user_id)}
                  disabled={!record.image_url}
                >
                  Double Print
                </Button>

                <Button
                  type={record.idcard_delivered ? 'default' : 'primary'}
                  disabled={record.idcard_delivered}
                  onClick={() => onMarkDelivered(record.id, userType === 'student')}
                >
                  {record.idcard_delivered ? 'Delivered' : 'Mark Delivered'}
                </Button>
              </Space>
            ),
          },
        ]}
      />

      {/* Upload Modal */}
      <Modal
        title="Upload ID Card Image"
        visible={uploadModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
      >
        {!uploadFile && (
          <Upload
            beforeUpload={file => {
              setUploadFile(file);
              setFileList([file]);
              return false;
            }}
            accept="image/*"
            maxCount={1}
            fileList={fileList}
            onRemove={() => {
              setUploadFile(null);
              setFileList([]);
            }}
            showUploadList={{ showRemoveIcon: true }}
          >
            <Button icon={<UploadOutlined />}>Select Image</Button>
          </Upload>
        )}

        {uploadFile && !isPreviewMode && (
          <>
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: 400,
                filter: `brightness(${brightness}%) contrast(${contrast}%)`,
                marginBottom: 16,
              }}
            >
              <Cropper
                image={URL.createObjectURL(uploadFile)}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                cropShape="rect"
                showGrid={false}
                objectFit="contain"
              />
            </div>

            {/* Controls */}
            <div style={{ marginBottom: 16 }}>
              <label>Zoom</label>
              <Slider
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(value) => setZoom(value as number)}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label>Brightness</label>
              <Slider
                min={50}
                max={200}
                step={1}
                value={brightness}
                onChange={(value) => setBrightness(value as number)}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label>Contrast</label>
              <Slider
                min={50}
                max={200}
                step={1}
                value={contrast}
                onChange={(value) => setContrast(value as number)}
              />
            </div>

            {/* Preview button */}
            <Button type="primary" onClick={async () => {
              await showCroppedImage();
              setIsPreviewMode(true);
            }}>
              Preview Cropped
            </Button>
          </>
        )}

        {uploadFile && isPreviewMode && croppedImageUrl && (
          <>
            <div style={{ marginBottom: 16 }}>
              <img src={croppedImageUrl} alt="Cropped Preview" style={{ maxWidth: '100%' }} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <Button onClick={() => setIsPreviewMode(false)}>Edit Again</Button>
              <Button onClick={downloadImage}>Download Cropped Image</Button>
              <Button type="primary" loading={loading} onClick={handleUpload}>
                Submit Upload
              </Button>
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default IdCardMaster;
